import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Button,
  message,
  Select,
  Space,
} from 'antd';
import ProductCollectionService from 'services/product_collection';
import TagsPicker from 'components/pickers/product_tag';
import CategoryPicker from 'components/pickers/product_category';
import ImagePicker from "components/pickers/image/ImagePicker";
import ProductPicker from 'components/pickers/product';

const service = new ProductCollectionService();

const ProductCollectionModal = ({ open, onClose, onChange, id }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (open && id) {
      const fetchData = async () => {
        try {
          const result = await service.get(id);
          var condition = result.collection_condition || {};
          var type = condition.type || 'manual';

          setData(result);
          form.setFieldsValue({
            title: result.title,
            description: result.description,
            condition: {
              type,
              product_ids: condition.product_ids || [],
              tags: condition.tags || [],
              categories: condition.categories || [],
            },
            avatar: result.avatar || result.image,
            banner: result.banner,
          });
        } catch (e) {
          console.error(e);
        }
      };
      fetchData();
    } else if (open) {
      setData(null);
      form.resetFields();
      form.setFieldsValue({
        condition: { type: 'manual', product_ids: [], tags: [], categories: [] },
      });
    }
  }, [open, id, form]);

  const handleTypeChange = () => {
    var type = form.getFieldValue(['condition', 'type']) || 'manual';
    form.setFieldsValue({
      condition: { type},
    });
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      var { condition: inputCondition = {}, ...rest } = values;
      var type = inputCondition.type || 'manual';
      var condition = { tags: [], categories: [], product_ids: [] };
      if (type === 'manual') {
        condition.product_ids = inputCondition.product_ids || [];
      }
      if (type === 'tag') {
        condition.tags = inputCondition.tags || [];
      }
      if (type === 'category') {
        condition.categories = inputCondition.categories || [];
      }
      condition.type = type;
      var payload = { ...rest, collection_condition: condition };
      var savedCollection = null;

      if (id) {
        await service.update({ id, ...payload });
        savedCollection = { id, ...payload };
      } else {
        savedCollection = await service.create({ ...payload });
      }

      form.resetFields();
      message.success(id ? 'Cập nhật collection thành công!' : 'Thêm collection thành công!');
      onChange?.(savedCollection);
      onClose();
    } catch (error) {
      message.error('Có lỗi xảy ra!');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={id ? 'Chỉnh sửa Collection' : 'Thêm Collection Mới'}
      open={open}
      onCancel={onClose}
      footer={null}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ condition: { type: 'manual', product_ids: [], tags: [], categories: [] } }}
      >
        <Form.Item
          label="Tên Collection"
          name="title"
          rules={[
            { required: true, message: 'Vui lòng nhập tên collection!' },
            { min: 3, message: 'Tên collection phải có ít nhất 3 ký tự!' },
          ]}
        >
          <Input placeholder="Nhập tên collection" />
        </Form.Item>

        <Form.Item
          label="Cách chọn sản phẩm"
          name={['condition', 'type']}
          rules={[{ required: true, message: 'Vui lòng chọn loại collection!' }]}
        >
          <Select
            onChange={handleTypeChange}
            options={[
              { label: 'Sản phẩm', value: 'manual' },
              { label: 'Tag', value: 'tag' },
              { label: 'Danh mục', value: 'category' },
            ]}
          />
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(prev, cur) => prev?.condition?.type !== cur?.condition?.type}>
          {() => {
            var type = form.getFieldValue(['condition', 'type']);
            return (
              <>
                <Form.Item
                  label="Sản phẩm"
                  name={['condition', 'product_ids']}
                  hidden={type !== 'manual'}
                  rules={
                    type === 'manual'
                      ? [{ required: true, message: 'Vui lòng chọn ít nhất 1 sản phẩm!' }]
                      : []
                  }
                >
                  <ProductPicker mode="multiple" placeholder="Chọn sản phẩm" />
                </Form.Item>

                <Form.Item
                  label="Tags"
                  name={['condition', 'tags']}
                  hidden={type !== 'tag'}
                  rules={
                    type === 'tag' ? [{ required: true, message: 'Vui lòng chọn ít nhất 1 tag!' }] : []
                  }
                >
                  <TagsPicker mode="multiple" placeholder="Chọn tags" />
                </Form.Item>

                <Form.Item
                  label="Danh mục"
                  name={['condition', 'categories']}
                  hidden={type !== 'category'}
                  rules={
                    type === 'category'
                      ? [{ required: true, message: 'Vui lòng chọn ít nhất 1 danh mục!' }]
                      : []
                  }
                >
                  <CategoryPicker mode="multiple" placeholder="Chọn danh mục" />
                </Form.Item>
              </>
            );
          }}
        </Form.Item>

        <Form.Item
          label="Mô tả"
          name="description"
        >
          <Input.TextArea
            placeholder="Nhập mô tả collection"
            rows={4}
          />
        </Form.Item>

        <Form.Item label="Hình đại diện" name="avatar"  help="Hình này sẽ được sử dụng làm ảnh đại diện cho collection, Đề xuất kích thước 500x500px">
          <ImagePicker />
        </Form.Item>

        <Form.Item label="Banner" name="banner" help="Hình này sẽ được sử dụng làm banner cho collection, Đề xuất kích thước 1200x400px">
          <ImagePicker />
        </Form.Item>

        <Form.Item>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={onClose}>Hủy</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {id ? 'Cập nhật' : 'Thêm'}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ProductCollectionModal;
